import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  limit,
  startAfter,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Tracking Analytics Service - Aggregates tracking link data for brand analytics
 */
const trackingAnalyticsService = {
  /**
   * Get tracking analytics for a specific brand's campaigns
   * @param {string} brandId - Brand user ID
   * @returns {Promise<object>} - Aggregated tracking analytics
   */
  getBrandTrackingAnalytics: async (brandId) => {
    try {
      // First, get all campaigns for this brand
      const campaignsQuery = query(
        collection(db, 'campaigns'),
        where('brandId', '==', brandId)
      );
      
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const campaignIds = [];
      campaignsSnapshot.forEach(doc => {
        campaignIds.push(doc.id);
      });

      if (campaignIds.length === 0) {
        return {
          totalClicks: 0,
          totalTrackingLinks: 0,
          clicksByPlatform: {},
          clicksByTimeframe: {},
          topPerformingLinks: [],
          campaignPerformance: []
        };
      }

      // Get all tracking links for these campaigns
      const trackingLinksQuery = query(
        collection(db, 'trackingLinks'),
        where('campaignId', 'in', campaignIds.slice(0, 10)), // Firestore 'in' limit is 10
        where('isActive', '==', true)
      );

      const trackingLinksSnapshot = await getDocs(trackingLinksQuery);
      const trackingLinks = [];
      let totalClicks = 0;
      
      trackingLinksSnapshot.forEach(doc => {
        const data = doc.data();
        trackingLinks.push({
          id: doc.id,
          ...data
        });
        totalClicks += data.analytics?.clicks || 0;
      });

      // Get detailed click data for analysis
      const clicksData = await trackingAnalyticsService.getDetailedClickAnalytics(trackingLinks.map(link => link.id));

      // Fetch influencer names for top performing links
      const topLinks = trackingLinks
        .sort((a, b) => (b.analytics?.clicks || 0) - (a.analytics?.clicks || 0))
        .slice(0, 10);

      // Get unique influencer IDs
      const influencerIds = [...new Set(topLinks.map(link => link.influencerId))];
      
      // Fetch influencer user data
      const influencerNames = {};
      if (influencerIds.length > 0) {
        const usersQuery = query(
          collection(db, 'users'),
          where('__name__', 'in', influencerIds)
        );
        
        const usersSnapshot = await getDocs(usersQuery);
        usersSnapshot.forEach(doc => {
          const userData = doc.data();
          influencerNames[doc.id] = userData.displayName || userData.firstName || userData.email || 'Unknown User';
        });
      }

      // Add influencer names to top performing links
      const topPerformingLinksWithNames = topLinks.map(link => ({
        ...link,
        influencerName: influencerNames[link.influencerId] || 'Unknown Influencer',
        clicks: link.analytics?.clicks || 0,
        ctr: link.analytics?.ctr || 0,
        lastClick: link.analytics?.lastClickedAt
      }));

      return {
        totalClicks,
        totalTrackingLinks: trackingLinks.length,
        clicksByPlatform: clicksData.platformBreakdown,
        clicksByTimeframe: clicksData.timeframeBreakdown,
        topPerformingLinks: topPerformingLinksWithNames,
        campaignPerformance: await trackingAnalyticsService.getCampaignTrackingPerformance(campaignIds, trackingLinks)
      };
    } catch (error) {
      console.error('Error getting brand tracking analytics:', error);
      throw error;
    }
  },

  /**
   * Get detailed click analytics from individual click records
   * @param {Array} trackingLinkIds - Array of tracking link IDs
   * @returns {Promise<object>} - Detailed click analytics
   */
  getDetailedClickAnalytics: async (trackingLinkIds) => {
    try {
      if (trackingLinkIds.length === 0) {
        return {
          platformBreakdown: {},
          timeframeBreakdown: {},
          referrerBreakdown: {}
        };
      }

      // Get click records (limit to recent clicks for performance)
      const clicksQuery = query(
        collection(db, 'trackingClicks'),
        where('trackingLinkId', 'in', trackingLinkIds.slice(0, 10)),
        orderBy('clickedAt', 'desc'),
        limit(1000) // Limit to recent 1000 clicks
      );

      const clicksSnapshot = await getDocs(clicksQuery);
      const clicks = [];
      
      clicksSnapshot.forEach(doc => {
        clicks.push(doc.data());
      });

      // Analyze platform breakdown
      const platformBreakdown = {};
      const timeframeBreakdown = {};
      const referrerBreakdown = {};

      clicks.forEach(click => {
        // Platform analysis
        const userAgent = click.userAgent || '';
        let platform = 'Unknown';
        
        if (userAgent.includes('Mobile') || userAgent.includes('Android') || userAgent.includes('iPhone')) {
          platform = 'Mobile';
        } else if (userAgent.includes('Tablet') || userAgent.includes('iPad')) {
          platform = 'Tablet';
        } else {
          platform = 'Desktop';
        }
        
        platformBreakdown[platform] = (platformBreakdown[platform] || 0) + 1;

        // Time analysis (last 30 days)
        const clickDate = click.clickedAt?.toDate?.() || new Date(click.clickedAt);
        const dayKey = clickDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        timeframeBreakdown[dayKey] = (timeframeBreakdown[dayKey] || 0) + 1;

        // Referrer analysis
        const referrer = click.referrer || 'Direct';
        let referrerSource = 'Direct';
        
        if (referrer.includes('instagram.com')) referrerSource = 'Instagram';
        else if (referrer.includes('tiktok.com')) referrerSource = 'TikTok';
        else if (referrer.includes('facebook.com')) referrerSource = 'Facebook';
        else if (referrer.includes('twitter.com') || referrer.includes('x.com')) referrerSource = 'X/Twitter';
        else if (referrer.includes('youtube.com')) referrerSource = 'YouTube';
        else if (referrer !== 'Direct') referrerSource = 'Other';
        
        referrerBreakdown[referrerSource] = (referrerBreakdown[referrerSource] || 0) + 1;
      });

      return {
        platformBreakdown,
        timeframeBreakdown,
        referrerBreakdown
      };
    } catch (error) {
      console.error('Error getting detailed click analytics:', error);
      return {
        platformBreakdown: {},
        timeframeBreakdown: {},
        referrerBreakdown: {}
      };
    }
  },

  /**
   * Get tracking performance by campaign
   * @param {Array} campaignIds - Array of campaign IDs
   * @param {Array} trackingLinks - Array of tracking link data
   * @returns {Promise<Array>} - Campaign performance data
   */
  getCampaignTrackingPerformance: async (campaignIds, trackingLinks) => {
    try {
      // Group tracking links by campaign
      const campaignPerformance = {};
      
      trackingLinks.forEach(link => {
        const campaignId = link.campaignId;
        if (!campaignPerformance[campaignId]) {
          campaignPerformance[campaignId] = {
            campaignId,
            totalClicks: 0,
            totalLinks: 0,
            averageClicksPerLink: 0
          };
        }
        
        campaignPerformance[campaignId].totalClicks += link.analytics?.clicks || 0;
        campaignPerformance[campaignId].totalLinks += 1;
      });

      // Calculate averages
      Object.keys(campaignPerformance).forEach(campaignId => {
        const perf = campaignPerformance[campaignId];
        perf.averageClicksPerLink = perf.totalLinks > 0 ? 
          Math.round(perf.totalClicks / perf.totalLinks) : 0;
      });

      return Object.values(campaignPerformance);
    } catch (error) {
      console.error('Error getting campaign tracking performance:', error);
      return [];
    }
  },

  /**
   * Get tracking analytics for a specific campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Campaign tracking analytics
   */
  getCampaignTrackingAnalytics: async (campaignId) => {
    try {
      // Get all tracking links for this campaign
      const trackingLinksQuery = query(
        collection(db, 'trackingLinks'),
        where('campaignId', '==', campaignId),
        where('isActive', '==', true)
      );

      const trackingLinksSnapshot = await getDocs(trackingLinksQuery);
      const trackingLinks = [];
      let totalClicks = 0;
      
      trackingLinksSnapshot.forEach(doc => {
        const data = doc.data();
        trackingLinks.push({
          id: doc.id,
          ...data
        });
        totalClicks += data.analytics?.clicks || 0;
      });

      // Get detailed analytics
      const detailedAnalytics = await trackingAnalyticsService.getDetailedClickAnalytics(
        trackingLinks.map(link => link.id)
      );

      return {
        totalClicks,
        totalInfluencers: trackingLinks.length,
        averageClicksPerInfluencer: trackingLinks.length > 0 ? 
          Math.round(totalClicks / trackingLinks.length) : 0,
        platformBreakdown: detailedAnalytics.platformBreakdown,
        referrerBreakdown: detailedAnalytics.referrerBreakdown,
        timeframeBreakdown: detailedAnalytics.timeframeBreakdown,
        influencerPerformance: trackingLinks.map(link => ({
          influencerId: link.influencerId,
          trackingCode: link.trackingCode,
          clicks: link.analytics?.clicks || 0,
          lastClickedAt: link.analytics?.lastClickedAt
        }))
      };
    } catch (error) {
      console.error('Error getting campaign tracking analytics:', error);
      throw error;
    }
  },

  /**
   * Get click trends over time for a date range
   * @param {string} brandId - Brand ID
   * @param {number} days - Number of days to look back (default 30)
   * @returns {Promise<Array>} - Daily click data
   */
  getClickTrends: async (brandId, days = 30) => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get campaigns for brand
      const campaignsQuery = query(
        collection(db, 'campaigns'),
        where('brandId', '==', brandId)
      );
      
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const campaignIds = [];
      campaignsSnapshot.forEach(doc => {
        campaignIds.push(doc.id);
      });

      if (campaignIds.length === 0) return [];

      // Get tracking links
      const trackingLinksQuery = query(
        collection(db, 'trackingLinks'),
        where('campaignId', 'in', campaignIds.slice(0, 10))
      );

      const trackingLinksSnapshot = await getDocs(trackingLinksQuery);
      const trackingLinkIds = [];
      trackingLinksSnapshot.forEach(doc => {
        trackingLinkIds.push(doc.id);
      });

      if (trackingLinkIds.length === 0) return [];

      // Get clicks within date range
      const clicksQuery = query(
        collection(db, 'trackingClicks'),
        where('trackingLinkId', 'in', trackingLinkIds.slice(0, 10)),
        where('clickedAt', '>=', Timestamp.fromDate(startDate)),
        orderBy('clickedAt', 'desc')
      );

      const clicksSnapshot = await getDocs(clicksQuery);
      const dailyClicks = {};

      clicksSnapshot.forEach(doc => {
        const click = doc.data();
        const clickDate = click.clickedAt?.toDate?.() || new Date(click.clickedAt);
        const dayKey = clickDate.toISOString().split('T')[0];
        
        dailyClicks[dayKey] = (dailyClicks[dayKey] || 0) + 1;
      });

      // Convert to array format for charts
      const trends = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayKey = date.toISOString().split('T')[0];
        
        trends.push({
          date: dayKey,
          clicks: dailyClicks[dayKey] || 0
        });
      }

      return trends;
    } catch (error) {
      console.error('Error getting click trends:', error);
      return [];
    }
  }
};

export default trackingAnalyticsService;
